import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card,
  Form,
  Switch,
  Input,
  Button,
  Space,
  Table,
  Tag,
  Spin,
  Result,
  Typography,
  message,
  Tooltip,
  Divider,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

type EndpointMapping = {
  handler?: string;
  path?: string;
  method?: string;
  enabled?: boolean;
  isOffer?: boolean;
  offerUrl?: string;
  offerError?: string;
};

type DynamicField = { name: string; value: string };

const tryParseJsonSafe = (s: string): { ok: boolean; error?: string } => {
  try {
    JSON.parse(s);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
};

export const AppParameters: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();

  const [allowInt, setAllowInt] = useState<boolean>(false);
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [whiteJson, setWhiteJson] = useState<string>("");
  const [grayJson, setGrayJson] = useState<string>("");
  const [initialSnapshot, setInitialSnapshot] = useState<{
    allowInt: boolean;
    debugMode: boolean;
    whiteJson: string;
    grayJson: string;
  } | null>(null);

  const appQuery = useQuery({
    queryKey: ["app", id],
    queryFn: () => api.getApp(id),
    enabled: Boolean(id),
  });

  const historyQuery = useQuery({
    queryKey: ["app-param-history", id],
    queryFn: () => api.getAppParameterHistory(id, 50),
    enabled: Boolean(id),
  });

  // Загружаем текущие значения из app.dynamicFields + endpointMappings
  useEffect(() => {
    const app = appQuery.data;
    if (!app) return;
    const df: DynamicField[] = (() => {
      const v: any = (app as any).dynamicFields;
      if (Array.isArray(v)) return v;
      if (typeof v === "string") {
        try {
          return JSON.parse(v);
        } catch {
          return [];
        }
      }
      return [];
    })();
    const em: EndpointMapping[] = (() => {
      const v: any = (app as any).endpointMappings;
      if (Array.isArray(v)) return v;
      if (typeof v === "string") {
        try {
          return JSON.parse(v);
        } catch {
          return [];
        }
      }
      return [];
    })();
    const fAllow =
      (df.find((f) => f?.name === "ALLOW_INT")?.value || "false") === "true";
    const fDebug =
      (df.find((f) => f?.name === "DEBUG_MODE")?.value || "false") === "true";
    const main =
      em.find(
        (m) => m?.handler === "handleGet" && m?.isOffer && m?.enabled !== false,
      ) || em.find((m) => m?.isOffer && m?.enabled !== false);
    const w = main?.offerError ?? "";
    const g = main?.offerUrl ?? "";

    setAllowInt(fAllow);
    setDebugMode(fDebug);
    setWhiteJson(w);
    setGrayJson(g);
    setInitialSnapshot({
      allowInt: fAllow,
      debugMode: fDebug,
      whiteJson: w,
      grayJson: g,
    });
  }, [appQuery.data]);

  const whiteParse = useMemo(() => tryParseJsonSafe(whiteJson), [whiteJson]);
  const grayParse = useMemo(() => tryParseJsonSafe(grayJson), [grayJson]);

  const isDirty = useMemo(() => {
    if (!initialSnapshot) return false;
    return (
      allowInt !== initialSnapshot.allowInt ||
      debugMode !== initialSnapshot.debugMode ||
      whiteJson !== initialSnapshot.whiteJson ||
      grayJson !== initialSnapshot.grayJson
    );
  }, [allowInt, debugMode, whiteJson, grayJson, initialSnapshot]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!initialSnapshot) throw new Error("No initial state");
      const payload: {
        allowInt?: boolean;
        debugMode?: boolean;
        whiteJson?: string;
        grayJson?: string;
      } = {};
      if (allowInt !== initialSnapshot.allowInt) payload.allowInt = allowInt;
      if (debugMode !== initialSnapshot.debugMode) payload.debugMode = debugMode;
      if (whiteJson !== initialSnapshot.whiteJson) payload.whiteJson = whiteJson;
      if (grayJson !== initialSnapshot.grayJson) payload.grayJson = grayJson;
      return api.updateAppParameters(id, payload);
    },
    onSuccess: (data) => {
      const n = data.changes.length;
      const deployed = data.deployed
        ? "redeploy ok"
        : "redeploy не пройшов (див. логи бэкенда)";
      messageApi.success(`Збережено ${n} змін(и). ${deployed}`);
      queryClient.invalidateQueries({ queryKey: ["app", id] });
      queryClient.invalidateQueries({ queryKey: ["app-param-history", id] });
      setInitialSnapshot({ allowInt, debugMode, whiteJson, grayJson });
    },
    onError: (err: any) => {
      messageApi.error(
        err?.response?.data?.message || err?.message || "Помилка збереження",
      );
    },
  });

  const purgeMutation = useMutation({
    mutationFn: () => api.purgeLfCache(id),
    onSuccess: (data) => {
      messageApi.success(
        `Кеш LF скинуто: видалено ${data.deleted} з ${data.matched} ключів (pattern: ${data.pattern})`,
      );
    },
    onError: (err: any) => {
      messageApi.error(
        err?.response?.data?.message ||
          err?.message ||
          "Помилка скидання кешу LF",
      );
    },
  });

  const formatJsonField = (raw: string) => {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  if (appQuery.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (appQuery.isError || !appQuery.data) {
    return (
      <Result
        status="error"
        title="Не вдалося завантажити застосунок"
        subTitle={(appQuery.error as any)?.message}
        extra={
          <Button onClick={() => navigate("/apps")}>До списку app'ів</Button>
        }
      />
    );
  }

  const app = appQuery.data;
  const canSave =
    isDirty &&
    whiteParse.ok &&
    grayParse.ok &&
    !saveMutation.isPending;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      {contextHolder}
      <Space style={{ marginBottom: 16 }} wrap>
        <Link to="/apps">
          <Button icon={<ArrowLeftOutlined />}>До списку</Button>
        </Link>
        <Title level={3} style={{ margin: 0 }}>
          {app.name}
        </Title>
        <Tag>{(app as any).customId}</Tag>
        <Tag color="blue">{(app as any).domain}</Tag>
      </Space>

      <Card
        title="Параметри застосунку"
        extra={
          <Tooltip title="Зміни зберігаються та автоматично деплояться на воркер">
            <Text type="secondary">Save = зберегти + redeploy</Text>
          </Tooltip>
        }
      >
        <Form layout="vertical">
          <Space size="large" wrap>
            <Form.Item label="ALLOW_INT" tooltip="Чи дозволена інтеграція з LinkFactory">
              <Switch checked={allowInt} onChange={setAllowInt} />
            </Form.Item>
            <Form.Item label="DEBUG_MODE" tooltip="Режим налагодження воркера">
              <Switch checked={debugMode} onChange={setDebugMode} />
            </Form.Item>
          </Space>

          <Form.Item
            label={
              <Space>
                <span>White JSON</span>
                <Text type="secondary">
                  (whitepage — коли LinkFactory відхиляє)
                </Text>
                {!whiteParse.ok && (
                  <Tag color="error">не валідний JSON: {whiteParse.error}</Tag>
                )}
              </Space>
            }
            help={
              !whiteParse.ok
                ? "Виправте JSON перш ніж зберегти"
                : "Підтримуються макроси {{TIMESTAMP}}, {{OFFER_ERROR}} тощо"
            }
            validateStatus={whiteParse.ok ? "" : "error"}
          >
            <TextArea
              value={whiteJson}
              onChange={(e) => setWhiteJson(e.target.value)}
              rows={6}
              spellCheck={false}
              style={{ fontFamily: "monospace" }}
            />
            <Button
              size="small"
              style={{ marginTop: 4 }}
              onClick={() => setWhiteJson(formatJsonField(whiteJson))}
              disabled={!whiteParse.ok}
            >
              Format JSON
            </Button>
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>Gray JSON</span>
                <Text type="secondary">(offer — коли LinkFactory схвалює)</Text>
                {!grayParse.ok && (
                  <Tag color="error">не валідний JSON: {grayParse.error}</Tag>
                )}
              </Space>
            }
            help={
              !grayParse.ok
                ? "Виправте JSON перш ніж зберегти"
                : "Підтримуються макроси {{OFFER_URL}}, {{TIMESTAMP}} тощо"
            }
            validateStatus={grayParse.ok ? "" : "error"}
          >
            <TextArea
              value={grayJson}
              onChange={(e) => setGrayJson(e.target.value)}
              rows={6}
              spellCheck={false}
              style={{ fontFamily: "monospace" }}
            />
            <Button
              size="small"
              style={{ marginTop: 4 }}
              onClick={() => setGrayJson(formatJsonField(grayJson))}
              disabled={!grayParse.ok}
            >
              Format JSON
            </Button>
          </Form.Item>

          <Divider />

          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={!canSave}
            >
              Save and apply
            </Button>
            <Button
              onClick={() => {
                if (!initialSnapshot) return;
                setAllowInt(initialSnapshot.allowInt);
                setDebugMode(initialSnapshot.debugMode);
                setWhiteJson(initialSnapshot.whiteJson);
                setGrayJson(initialSnapshot.grayJson);
              }}
              disabled={!isDirty || saveMutation.isPending}
            >
              Reset
            </Button>
            <Button
              danger
              loading={purgeMutation.isPending}
              onClick={() => purgeMutation.mutate()}
              title="Скинути Redis-кеш LinkFactory для домену цієї апки (як вчора робили через зміну LF_CACHING_KEY, тільки прямо)"
            >
              Скинути кеш LF
            </Button>
            {isDirty && (
              <Text type="warning">Є незбережені зміни</Text>
            )}
          </Space>
        </Form>
      </Card>

      <Card
        style={{ marginTop: 16 }}
        title={
          <Space>
            <HistoryOutlined />
            Історія змін
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={() => historyQuery.refetch()}
            loading={historyQuery.isFetching}
          >
            Оновити
          </Button>
        }
      >
        <Table
          rowKey="id"
          dataSource={historyQuery.data || []}
          loading={historyQuery.isLoading}
          size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: "Коли",
              dataIndex: "createdAt",
              key: "createdAt",
              width: 170,
              render: (v: string) => new Date(v).toLocaleString(),
            },
            {
              title: "Хто",
              dataIndex: "user",
              key: "user",
              width: 200,
              render: (u: any, row: any) => u?.email || row.userId,
            },
            {
              title: "Поле",
              dataIndex: "field",
              key: "field",
              width: 120,
              render: (v: string) => <Tag>{v}</Tag>,
            },
            {
              title: "Було",
              dataIndex: "before",
              key: "before",
              render: (v: string | null) => (
                <Paragraph
                  copyable={!!v}
                  ellipsis={{ rows: 2, expandable: true }}
                  style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}
                >
                  {v ?? <Text type="secondary">— (не задано)</Text>}
                </Paragraph>
              ),
            },
            {
              title: "Стало",
              dataIndex: "after",
              key: "after",
              render: (v: string | null) => (
                <Paragraph
                  copyable={!!v}
                  ellipsis={{ rows: 2, expandable: true }}
                  style={{ margin: 0, fontFamily: "monospace", fontSize: 12 }}
                >
                  {v ?? <Text type="secondary">— (не задано)</Text>}
                </Paragraph>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AppParameters;
